import { useState } from "react";
import { C } from "./theme";
import { todayKey, toKey } from "./dates";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_LABELS = [["S", "Sunday"], ["M", "Monday"], ["T", "Tuesday"], ["W", "Wednesday"], ["T", "Thursday"], ["F", "Friday"], ["S", "Saturday"]];

// A month grid with ‹ month › and "Today", used by the Goals calendar and Key Dates. `marks` maps a
// "YYYY-MM-DD" day to { color, label }: the day is filled with that colour and can be tapped to pick it
// (onSelectDay(day), or null to clear). Changing month clears the pick.
export function MonthCalendar({ marks, selectedDay, onSelectDay }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const today = todayKey();

  const show = (y, m) => { setYear(y); setMonth(m); onSelectDay(null); };
  const step = (delta) => { const d = new Date(year, month + delta, 1); show(d.getFullYear(), d.getMonth()); };

  const first = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);

  const arrow = { width: 44, height: 44, background: "none", border: "none", cursor: "pointer", color: C.textMid, fontSize: 24, lineHeight: 1, fontFamily: "inherit" };
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <button onClick={() => step(-1)} aria-label="Previous month" style={arrow}>‹</button>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2 aria-live="polite" style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text }}>{MONTH_NAMES[month]} {year}</h2>
          <button onClick={() => show(now.getFullYear(), now.getMonth())}
            style={{ minHeight: 44, fontSize: 12, fontWeight: 600, color: C.textGreen, background: C.accentLight, border: `1px solid ${C.textGreen}`, borderRadius: 12, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit" }}>
            Today
          </button>
        </div>
        <button onClick={() => step(1)} aria-label="Next month" style={arrow}>›</button>
      </div>

      <div aria-hidden="true" style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 4 }}>
        {DAY_LABELS.map(([l, name]) => (
          <div key={name} style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: C.textMid, padding: "2px 0" }}>{l}</div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, marginBottom: 20 }}>
        {cells.map((day, i) => {
          if (day === null) return <div key={i} />;
          const key = toKey(new Date(year, month, day));
          const mark = marks[key];
          const isToday = key === today;
          const isSelected = key === selectedDay;
          const cell = {
            aspectRatio: "1", width: "100%", display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: 6, border: "none", padding: 0, fontFamily: "inherit",
            background: mark ? mark.color : "transparent",
            outline: isToday && !mark ? `2px solid ${C.accent}` : undefined, outlineOffset: "-2px",
            boxShadow: isSelected ? `0 0 0 3px ${C.bg}, 0 0 0 6px ${C.text}` : "none",
            // Mark colours (goal colours, Key Dates) are mid tones in both themes, so their text is onTier.
            fontSize: 14, fontWeight: mark || isToday ? 700 : 400, color: mark ? C.onTier : C.textMid,
          };
          if (!mark) return <div key={i} style={cell} aria-label={isToday ? `Today, ${MONTH_NAMES[month]} ${day}` : undefined}>{day}</div>;
          const weekday = DAY_LABELS[new Date(year, month, day).getDay()][1];
          return (
            <button key={i} onClick={() => onSelectDay(isSelected ? null : key)} aria-pressed={isSelected}
              aria-label={`${weekday}, ${MONTH_NAMES[month]} ${day}: ${mark.label}`} style={{ ...cell, cursor: "pointer" }}>
              {day}
            </button>
          );
        })}
      </div>
    </>
  );
}
